import React from 'react'
import { createPortal } from 'react-dom'
import { ApiError, api } from '../api/client'
import type { PaidRung, PromptLock } from '../api/client'
import { useApp } from '../context/AppContext'
import { useDialog } from '../hooks/useDialog'
import { scrollToCardPayments, useCardAccount } from '../payments/useCardAccount'
import { saveErrorMessage } from '../screens/settings/toast'
import { IconButton } from '../ui/Icon'
import { rememberPending } from './pending'
import { RUNGS, UPGRADE_CONFIRM_BUTTON, VAT_LINE, buyLabel, longDate, paymentsButton, paymentsNote, paymentsStage, proButton, proNote, rungName, upgradeConfirm, upgradesInPlace, youreOn } from './rungs'
import ChargeConfirm from './ChargeConfirm'
import { startPayments, stripeSetupStep } from './startPayments'
import { reached, useBilling } from './useBilling'
import '../components/Sheet.css'
import './billing.css'

interface Props {
  rung: PaidRung
  lock: PromptLock
  // context is the line under the name: what the lock she tapped said, so
  // the sheet opens on it; else the rung's tagline.
  context?: string
  onClose: () => void
}

// RungSheet sells one rung from a lock: what it includes (brand/rungs.json),
// the price with the VAT line once, one button, and nothing else (spec 1.7).
// Opening it reports the tap once. The button goes to Stripe Checkout, or,
// when the API changed her subscription in place, says she has it. Going Pro
// from a Payments subscription charges her card at once, so the button first
// asks her to confirm.
export default function RungSheet({ rung, lock, context, onClose }: Props) {
  const { state, dispatch } = useApp()
  const { billing, follow } = useBilling()
  const periodEnd = billing?.subscription?.current_period_end
  const until = periodEnd ? longDate(periodEnd, state.me?.professional.timezone) : null
  // Both sheets follow the spec's order (section 1.1; Pro too since
  // 2026-10-25): Stripe's free setup first, then the price. Each says where
  // she is on that road, and its button is that road's next step (none
  // while Stripe checks her).
  const account = useCardAccount()
  const setupNote = rung === 'card_payments' ? paymentsNote(account) : proNote(account)
  const label = rung === 'card_payments' ? paymentsButton(account, true) : proButton(account, true, billing)
  const buying = paymentsStage(account) === 'buy'
  const [busy, setBusy] = React.useState(false)
  const [done, setDone] = React.useState(false)
  const [confirming, setConfirming] = React.useState(false)
  const [error, setError] = React.useState('')
  const asksFirst = rung === 'pro' && upgradesInPlace(billing)
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })

  const reported = React.useRef(false)
  React.useEffect(() => {
    if (reported.current) return
    reported.current = true
    api.reportPromptTap(lock, rung).catch(() => {})
  }, [lock, rung])

  // Either rung before Stripe has approved her: the sheet gives way to
  // Settings → Plan and payments at the Card payments card, where what Stripe
  // asks for, the button that starts its form, and its status live.
  const toSetup = (step: 'setup' | 'pending') => {
    onClose()
    dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'card-payments' })
    window.setTimeout(scrollToCardPayments, 400)
    return step
  }

  const buy = async () => {
    setBusy(true)
    setError('')
    try {
      if (rung === 'card_payments') {
        const step = await startPayments(account)
        if (step === 'setup' || step === 'pending') {
          toSetup(step)
          return
        }
        if (step === 'checkout') return
        setDone(true)
        void follow((b) => reached(b, rung))
        setBusy(false)
        return
      }
      const step = stripeSetupStep(account)
      if (step) {
        toSetup(step)
        return
      }
      const result = await api.startCheckout({ rung })
      if (result.url) {
        rememberPending(rung)
        window.location.assign(result.url)
        return
      }
      setDone(true)
      void follow((b) => reached(b, rung))
    } catch (e) {
      if (e instanceof ApiError && e.problem === 'already-on-rung') {
        setDone(true)
        void follow((b) => reached(b, rung))
      } else {
        setConfirming(false)
        setError(saveErrorMessage(e))
      }
    }
    setBusy(false)
  }

  const name = rungName(rung)
  return createPortal(
    <div className="sheet-overlay" onClick={() => { if (!busy) onClose() }}>
      <div
        ref={dialogRef}
        className="sheet-content sheet-content--closable rung-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={name}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle"></div>
        <IconButton icon="close" label="Close" className="sheet-close" onClick={onClose} disabled={busy} />
        <div className="sheet-header sheet-header--closable">
          <h2>{name}</h2>
          <p>{context ?? RUNGS[rung].tagline}</p>
        </div>
        <div className="sheet-body">
          <ul className="rung-sheet__includes">
            {RUNGS[rung].includes.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {done ? (
            <p className="rung-sheet__done" role="status">{youreOn(rung)}</p>
          ) : confirming ? (
            <ChargeConfirm
              label={buyLabel(rung, billing)}
              line={upgradeConfirm(until)}
              confirm={UPGRADE_CONFIRM_BUTTON}
              busy={busy}
              onConfirm={buy}
              onCancel={() => setConfirming(false)}
            />
          ) : (
            <>
              {setupNote && <p className="rung-sheet__note">{setupNote}</p>}
              {error && <p className="field-error" role="alert">{error}</p>}
              {label && (
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  onClick={() => {
                    if (asksFirst && buying) {
                      setError('')
                      setConfirming(true)
                    } else void buy()
                  }}
                  disabled={busy}
                >
                  {label}
                </button>
              )}
              <p className="rung-sheet__vat">{VAT_LINE}</p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
